import { useEffect, useId, useState, type MouseEvent as ReactMouseEvent, type ReactNode } from "react";
import { api } from "../api";

interface ListenersResponse {
  bucket: "minute" | "hour" | "day";
  from: string;
  to: string;
  points: Array<{ ts: string; listeners: number; sources: number }>;
  total: number;
  peak: number;
  peak_at: string | null;
  per_mount_peak: Array<{ mount: string; peak: number }>;
}

type Range = "1h" | "24h" | "7d" | "30d";

const RANGE_LABEL: Record<Range, string> = { "1h": "1 Std.", "24h": "24 Std.", "7d": "7 Tage", "30d": "30 Tage" };

const RANGE_MS: Record<Range, number> = {
  "1h": 60 * 60 * 1000,
  "24h": 24 * 60 * 60 * 1000,
  "7d": 7 * 24 * 60 * 60 * 1000,
  "30d": 30 * 24 * 60 * 60 * 1000,
};
const RANGE_BUCKET: Record<Range, "minute" | "hour" | "day"> = {
  "1h": "minute",
  "24h": "hour",
  "7d": "hour",
  "30d": "day",
};

export function ListenerStatsChart(): JSX.Element {
  const [range, setRange] = useState<Range>("24h");
  const [data, setData] = useState<ListenersResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const from = new Date(Date.now() - RANGE_MS[range]).toISOString();
    const to = new Date().toISOString();
    api.getListenerStats(from, to, RANGE_BUCKET[range])
      .then((d) => { setData(d); setError(null); })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [range]);

  return (
    <section className="card">
      <div className="card-header flex-wrap">
        <div className="min-w-0">
          <h2 className="card-title">Hörer-Statistik</h2>
          <p className="mt-0.5 text-xs text-muted">Wie viele gleichzeitig zugehört haben</p>
        </div>
        <div className="tabbar" role="tablist" aria-label="Zeitraum">
          {(["1h", "24h", "7d", "30d"] as Range[]).map((r) => (
            <button
              key={r}
              type="button"
              role="tab"
              aria-selected={range === r}
              onClick={() => setRange(r)}
              className={`tab !px-2.5 !py-1 text-xs ${range === r ? "tab-active" : ""}`}
              title={RANGE_LABEL[r]}
            >
              {r}
            </button>
          ))}
        </div>
      </div>
      <div className="card-body">
        {error && <div className="callout callout-danger mb-3 text-xs">{error}</div>}
        {!data && !error && <div className="h-[260px] animate-pulse rounded-lg bg-input" />}
        {data && <Chart data={data} range={range} />}
      </div>
    </section>
  );
}

function Kennzahl({ label, children }: { label: string; children: ReactNode }): JSX.Element {
  return (
    <div className="min-w-0">
      <div className="text-[0.75rem] font-medium text-muted">{label}</div>
      <div className="mt-0.5 truncate text-xl font-semibold tabular-nums tracking-tight text-primary">{children}</div>
    </div>
  );
}

function Chart({ data, range }: { data: ListenersResponse; range: Range }): JSX.Element {
  const verlauf = useId().replace(/:/g, "");
  const [hover, setHover] = useState<number | null>(null);

  if (data.points.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-default px-6 py-10 text-center">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="mb-2 text-muted" aria-hidden="true"><path d="M4 19h16M6 15l4-5 3 3 5-6" /></svg>
        <div className="text-sm font-medium text-primary">Keine Daten im Zeitraum.</div>
        <p className="mt-1 max-w-sm text-xs text-muted">(Stelle sicher, dass Icecast läuft und der Listener-Stats-Worker aktiv ist.)</p>
      </div>
    );
  }

  const W = 600;
  const H = 200;
  const PL = 30;
  const PR = 8;
  const PT = 10;
  const PB = 22;
  const max = Math.max(1, ...data.points.map((p) => p.listeners));
  const t0 = new Date(data.points[0]!.ts).getTime();
  const t1 = new Date(data.points[data.points.length - 1]!.ts).getTime();
  const span = Math.max(1, t1 - t0);

  const x = (ts: string): number => PL + ((new Date(ts).getTime() - t0) / span) * (W - PL - PR);
  const y = (v: number): number => H - PB - (v / max) * (H - PT - PB);

  // Path
  const path = data.points.map((p, i) => `${i === 0 ? "M" : "L"} ${x(p.ts).toFixed(1)} ${y(p.listeners).toFixed(1)}`).join(" ");
  // Area-Fill
  const area = `${path} L ${x(data.points[data.points.length - 1]!.ts).toFixed(1)} ${H - PB} L ${x(data.points[0]!.ts).toFixed(1)} ${H - PB} Z`;

  // Y-Achsen-Ticks (4 Stück)
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((f) => ({
    y: y(max * f),
    label: Math.round(max * f).toString(),
  }));

  // X-Achsen-Ticks (3 Stück)
  const xTickIdxs = [0, Math.floor(data.points.length / 2), data.points.length - 1];
  const zeit = (ts: string): string =>
    range === "1h" || range === "24h"
      ? new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      : new Date(ts).toLocaleString("de-DE", { day: "numeric", month: "short", hour: range === "7d" ? "2-digit" : undefined });

  // Schwebeanzeige: der nächstgelegene Messpunkt unter dem Mauszeiger.
  const onMove = (e: ReactMouseEvent<SVGSVGElement>): void => {
    const r = e.currentTarget.getBoundingClientRect();
    const vx = ((e.clientX - r.left) / r.width) * W;
    let best = 0;
    let dist = Infinity;
    data.points.forEach((p, i) => {
      const d = Math.abs(x(p.ts) - vx);
      if (d < dist) { dist = d; best = i; }
    });
    setHover(best);
  };
  const hp = hover !== null ? data.points[hover] : undefined;

  return (
    <div>
      <div className="mb-4 grid grid-cols-3 gap-4 border-b border-default pb-4">
        <Kennzahl label="Peak">{data.peak}</Kennzahl>
        <Kennzahl label="Summe">{data.total}</Kennzahl>
        <Kennzahl label="Peak Zeit">
          <span className="text-sm font-medium">{data.peak_at ? new Date(data.peak_at).toLocaleString() : "—"}</span>
        </Kennzahl>
      </div>

      <div className="relative">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="w-full touch-none"
          style={{ maxHeight: "220px" }}
          onMouseMove={onMove}
          onMouseLeave={() => setHover(null)}
          role="img"
          aria-label={`Hörerverlauf, höchstens ${data.peak} gleichzeitig`}
        >
          <defs>
            <linearGradient id={verlauf} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" style={{ stopColor: "rgb(var(--brand-primary-rgb))", stopOpacity: 0.28 }} />
              <stop offset="100%" style={{ stopColor: "rgb(var(--brand-primary-rgb))", stopOpacity: 0 }} />
            </linearGradient>
          </defs>
          {/* Y-Gridlines */}
          {yTicks.map((t, i) => (
            <g key={`y${i}`}>
              <line x1={PL} y1={t.y} x2={W - PR} y2={t.y} style={{ stroke: "var(--border)" }} strokeWidth="1" strokeDasharray={i === 0 ? undefined : "3 4"} />
              <text x={PL - 6} y={t.y + 3} textAnchor="end" fontSize="10" style={{ fill: "var(--text-muted)" }}>{t.label}</text>
            </g>
          ))}
          {/* Area */}
          <path d={area} fill={`url(#${verlauf})`} />
          {/* Line */}
          <path d={path} style={{ stroke: "rgb(var(--brand-primary-rgb))" }} strokeWidth="2" fill="none" strokeLinejoin="round" strokeLinecap="round" />
          {/* X-Labels */}
          {xTickIdxs.map((i, n) => {
            const p = data.points[i];
            if (!p) return null;
            return (
              <text key={`x${i}`} x={x(p.ts)} y={H - 5} textAnchor={n === 0 ? "start" : n === 2 ? "end" : "middle"} fontSize="10" style={{ fill: "var(--text-muted)" }}>
                {zeit(p.ts)}
              </text>
            );
          })}
          {hp && (
            <g aria-hidden="true">
              <line x1={x(hp.ts)} y1={PT} x2={x(hp.ts)} y2={H - PB} style={{ stroke: "var(--border-strong)" }} strokeWidth="1" />
              <circle cx={x(hp.ts)} cy={y(hp.listeners)} r="4" style={{ fill: "var(--bg-card)", stroke: "rgb(var(--brand-primary-rgb))" }} strokeWidth="2" />
            </g>
          )}
        </svg>
        {hp && (
          <div
            // Neben der Linie, nicht über dem Punkt – rechts, am rechten Rand links davon.
            className={`pointer-events-none absolute top-0 z-10 whitespace-nowrap rounded-lg border border-default bg-card px-2.5 py-1.5 text-xs shadow-md ${
              x(hp.ts) / W > 0.7 ? "-translate-x-[calc(100%+10px)]" : "translate-x-[10px]"
            }`}
            style={{ left: `${(x(hp.ts) / W) * 100}%` }}
          >
            <div className="font-semibold tabular-nums text-primary">{hp.listeners} Hörer</div>
            <div className="text-muted">{new Date(hp.ts).toLocaleString("de-DE", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</div>
          </div>
        )}
      </div>

      {data.per_mount_peak.length > 0 && (
        <div className="mt-4 border-t border-default pt-4">
          <div className="section-label mb-2">Top-Mounts (Peak)</div>
          <div className="space-y-1.5 text-xs">
            {data.per_mount_peak.slice(0, 5).map((m) => (
              <div key={m.mount} className="flex items-center gap-3">
                <span className="w-32 flex-shrink-0 truncate font-mono text-secondary sm:w-40">{m.mount}</span>
                <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-input">
                  <span className="block h-full rounded-full bg-brand-primary/70" style={{ width: `${Math.min(100, Math.round((m.peak / Math.max(1, data.peak)) * 100))}%` }} />
                </span>
                <span className="w-8 text-right font-mono tabular-nums text-primary">{m.peak}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
